import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isOverHero, setIsOverHero] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      setIsOverHero(y < window.innerHeight * 0.7);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const textColor = isOverHero && !mobileOpen
    ? 'text-white'
    : 'text-foreground-800';
  const mutedColor = isOverHero && !mobileOpen
    ? 'text-white/75'
    : 'text-foreground-600';
  const borderColor = isOverHero && !mobileOpen
    ? 'border-white/30'
    : 'border-foreground-300';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-10 h-[72px] flex items-center justify-between">
          {/* Brand */}
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2.5 group flex-shrink-0"
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${isOverHero && !mobileOpen ? 'bg-white/20' : 'bg-primary-500'}`}>
              <i className={`ri-leaf-line text-lg ${isOverHero && !mobileOpen ? 'text-white' : 'text-background-50'}`}></i>
            </div>
            <span className={`font-heading text-lg font-semibold whitespace-nowrap ${isOverHero && !mobileOpen ? 'text-white' : 'text-foreground-950'}`}>
              CSSBuySpreadsheet
            </span>
          </Link>

          {/* Desktop Center Nav */}
          <div className="hidden md:flex items-center gap-10">
            <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap ${mutedColor} hover:text-primary-500`}>
              Início
            </Link>
            <Link to="/guia-cssbuy-sheet/" className={`text-sm font-medium transition-colors whitespace-nowrap ${mutedColor} hover:text-primary-500`}>
              Guia de Compras
            </Link>
            <a href="#categorias" className={`text-sm font-medium transition-colors whitespace-nowrap ${mutedColor} hover:text-primary-500`}>
              Categorias
            </a>
            <a href="#artigos" className={`text-sm font-medium transition-colors whitespace-nowrap ${mutedColor} hover:text-primary-500`}>
              Artigos SEO
            </a>
            <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap ${mutedColor} hover:text-primary-500`}>
              FAQ
            </a>
          </div>

          {/* Right CTAs */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex items-center px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                isOverHero && !mobileOpen
                  ? 'text-white border border-white/40 hover:bg-white/15'
                  : 'text-foreground-700 border border-foreground-300 hover:border-primary-500 hover:text-primary-600'
              }`}
            >
              cssbuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                isOverHero && !mobileOpen
                  ? 'border-white/40 text-white'
                  : 'border-background-200 text-foreground-700'
              }`}
            >
              {mobileOpen ? (
                <i className="ri-close-line text-lg"></i>
              ) : (
                <i className="ri-menu-line text-lg"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        ></div>

        {/* Panel */}
        <div className={`absolute top-0 right-0 w-[85%] max-w-[380px] h-full bg-background-50 shadow-2xl transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full pt-[72px] pb-8 px-6 overflow-y-auto">
            {/* Main nav links */}
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-4">Navegação</p>
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                    <i className="ri-home-line"></i>
                  </div>
                  Início
                </Link>
                <Link
                  to="/guia-cssbuy-sheet/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-book-open-line"></i>
                  </div>
                  Guia de Compras
                </Link>
              </div>
            </div>

            {/* Categories */}
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-4">Categorias</p>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    <span className="truncate">{cat.name}</span>
                    <i className="ri-arrow-right-s-line text-foreground-400 ml-auto flex-shrink-0"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-4">Links Rápidos</p>
              <div className="space-y-1">
                {[
                  { label: 'Artigos SEO', href: '#artigos', icon: 'ri-article-line' },
                  { label: 'FAQ', href: '#faq', icon: 'ri-question-line' },
                  { label: 'Segurança', href: '#seguranca', icon: 'ri-shield-check-line' },
                  { label: 'Comparação', href: '#comparacao', icon: 'ri-scales-line' },
                  { label: 'Reviews', href: '#reviews', icon: 'ri-star-line' },
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                      <i className={`${link.icon} text-xs`}></i>
                    </div>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Mobile CTAs */}
            <div className="mt-auto space-y-3 pt-6 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-foreground-700 border border-foreground-300 rounded-xl hover:border-primary-500 hover:text-primary-600 transition-all"
              >
                cssbuy sheet
                <i className="ri-external-link-line text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-xl hover:bg-primary-600 transition-all"
              >
                <i className="ri-shopping-bag-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}